<template>
    <div>
        <div class="text-center mb-6">
            <v-icon size="48" color="primary" class="mb-2">mdi-credit-card-outline</v-icon>
            <h3 class="text-h6 font-weight-bold">Add Payment Method</h3>
            <p class="text-body-2 text-grey-darken-1">A payment method is required to activate your tracker subscription</p>
        </div>

        <v-alert v-if="errors.length" class="mb-4" type="error" density="compact" variant="tonal" closable>
            <div v-for="error in errors" :key="error">{{ error }}</div>
        </v-alert>

        <v-text-field
            v-model="cardName"
            label="Name on Card"
            prepend-inner-icon="mdi-account"
            variant="outlined"
            class="mb-3"
            autofocus
        />

        <div class="stripe-card-wrapper mb-4">
            <div ref="cardElement" class="stripe-card-element"/>
        </div>

        <v-btn
            color="primary"
            size="large"
            block
            :disabled="!cardReady || !cardName || loading"
            :loading="loading"
            @click="submitCard"
            class="mb-4"
        >
            <v-icon start>mdi-shield-check</v-icon>
            Add Payment Method
        </v-btn>

        <!-- Trust signals -->
        <div class="trust-section pa-4 rounded-lg mb-6">
            <div class="d-flex align-center mb-2">
                <v-icon size="18" color="blue-darken-2" class="mr-2">mdi-shield-check</v-icon>
                <span class="text-body-2 font-weight-medium" style="color: #1565C0">
                    Your card details are sent directly to Stripe and never stored on our servers
                </span>
            </div>
            <div class="d-flex align-center mb-1 ml-7">
                <v-icon size="14" color="grey-darken-1" class="mr-2">mdi-certificate</v-icon>
                <span class="text-caption text-grey-darken-1">PCI DSS Level 1 certified - the highest level of payment security</span>
            </div>
            <div class="d-flex align-center mb-3 ml-7">
                <v-icon size="14" color="grey-darken-1" class="mr-2">mdi-lock</v-icon>
                <span class="text-caption text-grey-darken-1">Encrypted with 256-bit SSL - the same security used by banks</span>
            </div>
            <div class="d-flex align-center justify-center text-caption text-grey" style="gap: 6px">
                <span>Powered by</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="17" viewBox="0 0 60 25" fill="#635BFF">
                    <path d="M5 10.2c0-.7.6-1 1.5-1 1.3 0 3 .4 4.3 1.1V6.7c-1.4-.6-2.9-.8-4.3-.8C3.2 5.9.8 7.8.8 10.4c0 4.1 5.6 3.4 5.6 5.2 0 .9-.8 1.1-1.8 1.1-1.6 0-3.6-.6-5.2-1.5v3.6c1.8.8 3.5 1.1 5.2 1.1 3.4 0 5.8-1.7 5.8-4.4-.1-4.3-5.6-3.6-5.6-5.3zM14.4 3.8l-4 .9v3.7l4-.9V3.8zM10.4 9.6h4v10.2h-4V9.6zM19.2 9.6l-.3-1h-3.5v10.2h4v-6.9c.9-1.2 2.5-1 3-.8V7.6c-.5-.2-2.5-.6-3.2 2zM24.1 5.9l-4 .8v9.3c0 1.7 1.3 3 3 3 1 0 1.7-.2 2.1-.4v-3.1c-.4.1-2.2.6-2.2-1V12h2.2V9.6h-2.2V5.9zM32.8 12.5c0-.5.4-.7.9-.7.8 0 1.8.3 2.6.7V9.7c-.9-.4-1.7-.5-2.6-.5-2.1 0-3.6 1.1-3.6 3 0 2.9 4 2.5 4 3.7 0 .6-.5.8-1.2.8-1 0-2.3-.4-3.3-1v2.7c1.1.5 2.3.7 3.3.7 2.2 0 3.7-1.1 3.7-3 0-3.2-4-2.6-4-3.8l.2.2z"/>
                </svg>
            </div>
        </div>

        <div class="buy-device-cta pa-5 text-center rounded-xl">
            <p class="text-body-2 text-grey-darken-2 mb-3">Don't have a tracking device yet?</p>
            <v-btn
                href="https://simpligps.com/shop-gps-trackers/"
                target="_blank"
                color="deep-purple-accent-4"
                variant="tonal"
                size="large"
                rounded="pill"
                class="px-8"
            >
                <v-icon start>mdi-shopping</v-icon>
                Shop Devices
                <v-icon end size="16">mdi-open-in-new</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, nextTick} from "vue";
import {send} from "@/composables/apiClient";
import {loadStripe} from '@stripe/stripe-js';
import {useSnackbarStore} from "@/stores/snackBarStore";

const emit = defineEmits(['paymentAdded']);
const snackbar = useSnackbarStore();

const cardElement = ref(null);
const cardName = ref('');
const cardReady = ref(false);
const loading = ref(false);
const errors = ref([]);

let stripe = null;
let elements = null;
let card = null;
let setupIntent = null;

onMounted(async () => {
    try {
        stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
        if (!stripe) {
            errors.value.push('Payment system could not be loaded. Please disable any ad blockers and refresh the page.');
            return;
        }
        elements = stripe.elements();
        card = elements.create('card', {
            hidePostalCode: true,
            style: {
                base: {
                    fontSize: '16px',
                    fontFamily: 'Avenir, Helvetica, Arial, sans-serif',
                    '::placeholder': {color: '#96999C'},
                },
                invalid: {color: '#F10505', iconColor: '#F10505'},
            },
        });
        card.on('change', (event) => {
            errors.value = [];
            if (event.error) {
                errors.value.push(event.error.message);
                cardReady.value = false;
            } else if (event.complete) {
                cardReady.value = true;
            } else {
                cardReady.value = false;
            }
        });
        await nextTick();
        card.mount(cardElement.value);
    } catch (e) {
        errors.value.push('Payment system failed to initialize. Please refresh the page and try again.');
    }
});

onBeforeUnmount(() => {
    if (card) card.destroy();
});

const submitCard = async () => {
    errors.value = [];
    const trimmedName = cardName.value?.trim();
    if (!trimmedName) {
        errors.value.push('Card holder name is required');
        return;
    }

    loading.value = true;
    try {
        const intentResponse = await send('post', 'payments/create-setup-intent');
        setupIntent = intentResponse.data.data;

        const {setupIntent: confirmed, error} = await stripe.confirmCardSetup(
            setupIntent.client_secret,
            {
                payment_method: {
                    card: card,
                    billing_details: {name: trimmedName},
                },
            }
        );

        if (error) {
            errors.value.push(error.message);
            return;
        }

        await send('post', 'payments/create-method', {
            payment_method: confirmed.payment_method,
        });

        snackbar.showSnackbar('Payment method added successfully');
        emit('paymentAdded');
    } catch (e) {
        errors.value.push(e.response?.data?.message || 'Something went wrong while adding your payment method. Please try again.');
        snackbar.showSnackbar('Payment method could not be added', 'error');
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
.stripe-card-wrapper {
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 14px 12px;
    background: white;
}

.stripe-card-element {
    min-height: 24px;
}

.trust-section {
    background: rgba(227, 242, 253, 0.5);
    border: 1px solid #BBDEFB;
}

.buy-device-cta {
    background: linear-gradient(135deg, #f3f0ff 0%, #ede7ff 100%);
    border: 1px dashed #b39ddb;
}
</style>
